import { writable, derived, get } from 'svelte/store';
import { supabase } from './supabase';
import { offlineStorage, type SyncQueueItem } from './offline-storage';
import type { Flashcard, FlashcardTargetLanguage, FlashcardCategory } from '../types/flashcard';

/**
 * Sync Service
 *
 * Manages synchronization between local offline storage and remote Supabase database.
 * Handles online/offline detection, sync queue processing, and conflict resolution.
 */

// Network status store
export const isOnline = writable(navigator.onLine);
export const isSyncing = writable(false);
export const lastSyncTime = writable<number | null>(null);
export const syncError = writable<string | null>(null);
export const pendingSyncCount = writable(0);

// Derived store for connection status message
export const connectionStatus = derived(
  [isOnline, isSyncing, pendingSyncCount],
  ([$isOnline, $isSyncing, $pendingSyncCount]) => {
    if ($isSyncing) return 'Synchronizuji...';
    if (!$isOnline) return 'Offline';
    if ($pendingSyncCount > 0) return `Čeká ${$pendingSyncCount} změn`;
    return 'Online';
  }
);

class SyncService {
  private syncInProgress = false;
  private autoSyncInterval: number | null = null;

  constructor() {
    this.setupNetworkListeners();
    this.loadLastSyncTime();
    this.updatePendingSyncCount();
  }

  /**
   * Setup network status listeners
   */
  private setupNetworkListeners() {
    window.addEventListener('online', () => {
      console.log('📡 Network: Online');
      isOnline.set(true);
      syncError.set(null);
      // Auto-sync when coming back online
      this.sync();
    });

    window.addEventListener('offline', () => {
      console.log('📡 Network: Offline');
      isOnline.set(false);
    });

    // Initial status
    isOnline.set(navigator.onLine);
  }

  /**
   * Load last sync time from storage
   */
  private async loadLastSyncTime() {
    const lastSync = await offlineStorage.getLastSync();
    lastSyncTime.set(lastSync);
  }

  /**
   * Update pending sync count
   */
  private async updatePendingSyncCount() {
    const queue = await offlineStorage.getSyncQueue();
    pendingSyncCount.set(queue.length);
  }

  /**
   * Start auto-sync (every 5 minutes when online)
   */
  startAutoSync(intervalMs: number = 5 * 60 * 1000) {
    if (this.autoSyncInterval !== null) {
      clearInterval(this.autoSyncInterval);
    }

    this.autoSyncInterval = window.setInterval(() => {
      if (get(isOnline)) {
        this.sync();
      }
    }, intervalMs);
  }

  /**
   * Stop auto-sync
   */
  stopAutoSync() {
    if (this.autoSyncInterval !== null) {
      clearInterval(this.autoSyncInterval);
      this.autoSyncInterval = null;
    }
  }

  /**
   * Main sync function - bidirectional sync
   */
  async sync(): Promise<void> {
    // Don't sync if offline or already syncing
    if (!get(isOnline) || this.syncInProgress) {
      return;
    }

    try {
      this.syncInProgress = true;
      isSyncing.set(true);
      syncError.set(null);

      console.log('🔄 Starting sync...');

      // Step 1: Process outgoing sync queue (local → remote)
      await this.processSyncQueue();

      // Step 2: Fetch and cache remote data (remote → local)
      await this.fetchAndCacheData();

      // Step 3: Update last sync time
      await offlineStorage.updateLastSync();
      lastSyncTime.set(Date.now());

      console.log('✅ Sync completed successfully');
    } catch (error) {
      console.error('❌ Sync error:', error);
      syncError.set(error instanceof Error ? error.message : 'Chyba synchronizace');
    } finally {
      this.syncInProgress = false;
      isSyncing.set(false);
      await this.updatePendingSyncCount();
    }
  }

  /**
   * Process sync queue - send local changes to server
   */
  private async processSyncQueue(): Promise<void> {
    const queue = await offlineStorage.getSyncQueue();

    if (queue.length === 0) {
      return;
    }

    console.log(`📤 Processing ${queue.length} items in sync queue...`);

    for (const item of queue) {
      try {
        await this.processSyncItem(item);
        await offlineStorage.removeFromSyncQueue(item.id);
        console.log(`✅ Synced: ${item.action}`);
      } catch (error) {
        console.error(`❌ Failed to sync ${item.action}:`, error);

        // Retry logic - max 3 retries
        if (item.retryCount < 3) {
          await offlineStorage.updateSyncQueueItemRetry(item.id);
        } else {
          console.error(`🚫 Max retries reached for ${item.id}, removing from queue`);
          await offlineStorage.removeFromSyncQueue(item.id);
        }
      }
    }
  }

  /**
   * Process a single sync queue item
   */
  private async processSyncItem(item: SyncQueueItem): Promise<void> {
    const { action, data } = item;

    switch (action) {
      case 'create_flashcard':
        await this.syncCreateFlashcard(data);
        break;
      case 'update_flashcard':
        await this.syncUpdateFlashcard(data);
        break;
      case 'delete_flashcard':
        await this.syncDeleteFlashcard(data);
        break;
      case 'create_category':
        await this.syncCreateCategory(data);
        break;
      case 'update_category':
        await this.syncUpdateCategory(data);
        break;
      case 'delete_category':
        await this.syncDeleteCategory(data);
        break;
      default:
        console.warn(`Unknown sync action: ${action}`);
    }
  }

  /**
   * Sync operations for flashcards
   */
  private async syncCreateFlashcard(flashcard: Flashcard): Promise<void> {
    const {
      data: { user },
    } = await supabase.auth.getUser();
    if (!user) throw new Error('Not authenticated');

    const { error } = await supabase.from('flashcards').insert({
      user_id: user.id,
      target_language_id: flashcard.targetLanguage.id,
      category_id: flashcard.category?.id || null,
      primary_text: flashcard.primary,
      target_text: flashcard.target,
      phonetic: flashcard.phonetic || null,
      audio_url: flashcard.audioUrl || null,
      is_learned: flashcard.isLearned,
      examples: flashcard.examples || [],
      // FSRS fields
      due: flashcard.due?.toISOString() || null,
      stability: flashcard.stability || 0,
      difficulty: flashcard.difficulty || 0,
      elapsed_days: flashcard.elapsed_days || 0,
      scheduled_days: flashcard.scheduled_days || 0,
      reps: flashcard.reps || 0,
      lapses: flashcard.lapses || 0,
      state: flashcard.state || 'New',
      last_review: flashcard.last_review?.toISOString() || null,
    });

    if (error) throw error;
  }

  private async syncUpdateFlashcard(flashcard: Flashcard): Promise<void> {
    const { error } = await supabase
      .from('flashcards')
      .update({
        primary_text: flashcard.primary,
        target_text: flashcard.target,
        phonetic: flashcard.phonetic || null,
        audio_url: flashcard.audioUrl || null,
        is_learned: flashcard.isLearned,
        examples: flashcard.examples || [],
        category_id: flashcard.category?.id || null,
        // FSRS fields
        due: flashcard.due?.toISOString() || null,
        stability: flashcard.stability || 0,
        difficulty: flashcard.difficulty || 0,
        elapsed_days: flashcard.elapsed_days || 0,
        scheduled_days: flashcard.scheduled_days || 0,
        reps: flashcard.reps || 0,
        lapses: flashcard.lapses || 0,
        state: flashcard.state || 'New',
        last_review: flashcard.last_review?.toISOString() || null,
      })
      .eq('id', flashcard.id);

    if (error) throw error;
  }

  private async syncDeleteFlashcard(flashcardId: string): Promise<void> {
    const { error } = await supabase.from('flashcards').delete().eq('id', flashcardId);

    if (error) throw error;
  }

  /**
   * Sync operations for categories
   */
  private async syncCreateCategory(category: FlashcardCategory): Promise<void> {
    const {
      data: { user },
    } = await supabase.auth.getUser();
    if (!user) throw new Error('Not authenticated');

    const { error } = await supabase.from('flashcard_categories').insert({
      user_id: user.id,
      target_language_id: category.targetLanguage.id,
      name: category.name,
      description: category.description || null,
    });

    if (error) throw error;
  }

  private async syncUpdateCategory(category: FlashcardCategory): Promise<void> {
    const { error } = await supabase
      .from('flashcard_categories')
      .update({
        name: category.name,
        description: category.description || null,
      })
      .eq('id', category.id);

    if (error) throw error;
  }

  private async syncDeleteCategory(categoryId: string): Promise<void> {
    const { error } = await supabase.from('flashcard_categories').delete().eq('id', categoryId);

    if (error) throw error;
  }

  /**
   * Fetch data from server and cache locally
   */
  private async fetchAndCacheData(): Promise<void> {
    const {
      data: { user },
    } = await supabase.auth.getUser();
    if (!user) {
      console.log('⚠️ Not authenticated, skipping data fetch');
      return;
    }

    console.log('📥 Fetching data from server...');

    // Fetch target languages first (needed for transforming other data)
    const { data: languagesData, error: languagesError } = await supabase
      .from('flashcard_target_languages')
      .select('*');

    if (languagesError) {
      console.error('Error fetching languages:', languagesError);
      return;
    }

    const languages: FlashcardTargetLanguage[] = languagesData.map((l) => ({
      id: l.id,
      name: l.name,
      code: l.code,
      is_custom: l.is_custom,
    }));
    await offlineStorage.saveLanguages(languages);
    console.log(`✅ Cached ${languages.length} languages`);

    // Create language lookup map
    const languageMap = new Map(languages.map((lang) => [lang.id, lang]));

    // Fetch categories
    const { data: categoriesData, error: categoriesError } = await supabase
      .from('flashcard_categories')
      .select('*')
      .eq('user_id', user.id);

    let categories: FlashcardCategory[] = [];
    if (categoriesError) {
      console.error('Error fetching categories:', categoriesError);
    } else if (categoriesData) {
      categories = categoriesData
        .map((c) => {
          const targetLanguage = languageMap.get(c.target_language_id);
          if (!targetLanguage) {
            console.warn('⚠️ Category without matching language:', c);
            return null;
          }
          return {
            id: c.id,
            name: c.name,
            description: c.description || undefined,
            targetLanguage,
            flashcards: [],
            createdAt: new Date(c.created_at),
            updatedAt: new Date(c.updated_at),
          };
        })
        .filter(Boolean) as FlashcardCategory[];

      await offlineStorage.saveCategories(categories);
      console.log(`✅ Cached ${categories.length} categories`);
    }

    // Create category lookup map
    const categoryMap = new Map(categories.map((cat) => [cat.id, cat]));

    // Fetch flashcards
    const { data: flashcardsData, error: flashcardsError } = await supabase
      .from('flashcards')
      .select('*')
      .eq('user_id', user.id);

    if (flashcardsError) {
      console.error('Error fetching flashcards:', flashcardsError);
    } else if (flashcardsData) {
      const skippedFlashcards: string[] = [];
      const flashcards: Flashcard[] = flashcardsData
        .map((f) => {
          const targetLanguage = languageMap.get(f.target_language_id);
          if (!targetLanguage) {
            console.warn(
              `⚠️ Flashcard without matching language (${f.target_language_id}): "${f.primary_text}" → "${f.target_text}"`,
              f.id
            );
            skippedFlashcards.push(f.id);
            return null;
          }
          const category = f.category_id ? categoryMap.get(f.category_id) : undefined;

          return {
            id: f.id,
            primary: f.primary_text,
            target: f.target_text,
            phonetic: f.phonetic || '',
            audioUrl: f.audio_url || undefined,
            isLearned: f.is_learned,
            targetLanguage,
            category,
            examples: Array.isArray(f.examples) ? (f.examples as string[]) : [],
            createdAt: new Date(f.created_at),
            updatedAt: new Date(f.updated_at),
            // FSRS fields
            due: f.due ? new Date(f.due) : undefined,
            stability: f.stability || undefined,
            difficulty: f.difficulty || undefined,
            elapsed_days: f.elapsed_days || undefined,
            scheduled_days: f.scheduled_days || undefined,
            reps: f.reps || undefined,
            lapses: f.lapses || undefined,
            state: f.state || undefined,
            last_review: f.last_review ? new Date(f.last_review) : undefined,
          };
        })
        .filter(Boolean) as Flashcard[];

      if (skippedFlashcards.length > 0) {
        console.warn(
          `⚠️ Skipped ${skippedFlashcards.length} flashcards with missing language references. These need to be cleaned up or language references restored.`
        );
      }

      await offlineStorage.saveFlashcards(flashcards);
      console.log(`✅ Cached ${flashcards.length}/${flashcardsData.length} flashcards`);
    }
  }

  /**
   * Force a full sync now
   */
  async forceSyncNow(): Promise<void> {
    await this.sync();
  }

  /**
   * Get storage statistics
   */
  async getStats() {
    return await offlineStorage.getStorageStats();
  }

  /**
   * Clear all offline data
   */
  async clearOfflineData(): Promise<void> {
    await offlineStorage.clearAll();
    lastSyncTime.set(null);
    pendingSyncCount.set(0);
  }
}

// Export singleton instance
export const syncService = new SyncService();
